// Decimal strings. The API sends every quantity, price and amount as a decimal
// STRING (NUMERIC(38,18) on the server). Nothing here ever touches a JS float:
// formatting works on the digit string, comparison and the few preview sums use
// exact BigInt arithmetic. The server is authoritative for every stored number;
// the arithmetic helpers exist only for (a) sorting, (b) allocation-limit
// validation and (c) the double-entry PREVIEW on the entry form.

const DEC_RE = /^[+-]?(\d+)(?:\.(\d+))?$/;

export function isDecimalString(s: unknown): s is string {
  return typeof s === 'string' && DEC_RE.test(s.trim());
}

interface Parsed {
  neg: boolean;
  int: string;
  frac: string;
}

function parse(s: string): Parsed {
  const m = DEC_RE.exec(s.trim());
  if (!m) throw new Error(`not a decimal string: ${JSON.stringify(s)}`);
  const neg = s.trim().startsWith('-');
  return { neg, int: (m[1] ?? '0').replace(/^0+(?=\d)/, ''), frac: m[2] ?? '' };
}

function toScaled(p: Parsed, scale: number): bigint {
  const frac = p.frac.padEnd(scale, '0');
  const v = BigInt(p.int + frac);
  return p.neg ? -v : v;
}

function fromScaled(v: bigint, scale: number): string {
  const neg = v < 0n;
  const abs = (neg ? -v : v).toString().padStart(scale + 1, '0');
  const int = abs.slice(0, abs.length - scale);
  const frac = abs.slice(abs.length - scale).replace(/0+$/, '');
  const body = frac ? `${int}.${frac}` : int;
  return neg && body !== '0' ? `-${body}` : body;
}

function pair(a: string, b: string): [bigint, bigint, number] {
  const pa = parse(a);
  const pb = parse(b);
  const scale = Math.max(pa.frac.length, pb.frac.length);
  return [toScaled(pa, scale), toScaled(pb, scale), scale];
}

export function compareDecimal(a: string, b: string): -1 | 0 | 1 {
  const [x, y] = pair(a, b);
  return x < y ? -1 : x > y ? 1 : 0;
}

export function addDecimal(a: string, b: string): string {
  const [x, y, scale] = pair(a, b);
  return fromScaled(x + y, scale);
}

export function subDecimal(a: string, b: string): string {
  const [x, y, scale] = pair(a, b);
  return fromScaled(x - y, scale);
}

export function mulDecimal(a: string, b: string): string {
  const pa = parse(a);
  const pb = parse(b);
  const scale = pa.frac.length + pb.frac.length;
  const v = toScaled(pa, pa.frac.length) * toScaled(pb, pb.frac.length);
  return fromScaled(v, scale);
}

export function negateDecimal(a: string): string {
  const p = parse(a);
  return fromScaled(-toScaled(p, p.frac.length), p.frac.length);
}

export function sumDecimals(values: readonly string[]): string {
  return values.reduce((acc, v) => addDecimal(acc, v), '0');
}

export function isZeroDecimal(a: string): boolean {
  return compareDecimal(a, '0') === 0;
}

export function isNegativeDecimal(a: string): boolean {
  return compareDecimal(a, '0') < 0;
}

export interface FormatOptions {
  /** Fixed number of decimals. Omit for "as many as needed, max `maxDp`". */
  dp?: number;
  /** Upper bound when `dp` is omitted (trailing zeros are trimmed). */
  maxDp?: number;
  /** Prefix + for positive values. */
  sign?: boolean;
}

const NBSP = ' ';
const MINUS = '−';

function groupInt(int: string): string {
  return int.replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
}

/** Round half away from zero on the digit string, to `dp` decimals. */
function roundTo(p: Parsed, dp: number): { int: string; frac: string } {
  if (p.frac.length <= dp) return { int: p.int, frac: p.frac.padEnd(dp, '0') };
  const keep = p.int + p.frac.slice(0, dp);
  const next = p.frac.charCodeAt(dp) - 48;
  let v = BigInt(keep);
  if (next >= 5) v += 1n;
  const s = v.toString().padStart(dp + 1, '0');
  return { int: s.slice(0, s.length - dp), frac: s.slice(s.length - dp) };
}

/** Display formatting: "163 500", "431,80", "−0,5". Hungarian separators. */
export function formatDecimal(value: string, opts: FormatOptions = {}): string {
  const p = parse(value);
  const maxDp = opts.maxDp ?? 8;
  const r = opts.dp !== undefined ? roundTo(p, opts.dp) : roundTo(p, Math.min(p.frac.length, maxDp));
  let frac = r.frac;
  if (opts.dp === undefined) frac = frac.replace(/0+$/, '');
  const isZero = /^0*$/.test(r.int) && /^0*$/.test(frac);
  const body = groupInt(r.int) + (frac ? `,${frac}` : '');
  if (isZero) return body;
  if (p.neg) return `${MINUS}${body}`;
  return opts.sign ? `+${body}` : body;
}

/** Percentage strings arrive as decimals already in percent (e.g. "0.96"). */
export function formatPercent(value: string, dp = 2): string {
  return `${formatDecimal(value, { dp, sign: true })}%`;
}
